<template>
  <figure class="qr-card">
    <img
      class="qr-card-code"
      :src="src"
      :alt="t('QR code that opens {url}', { url })"
      decoding="async"
    />
  </figure>
</template>

<style lang="scss" scoped>
@import '~/_variables.scss';

/* The code that takes a reader from this page to the client's site on a phone.

   A case page is read at a desk and the site it is about is a phone-sized
   experience, so the one thing the page cannot show is the site in a hand. The
   code is that door, and it is the only object on the page whose surface is not
   the page's: the tile behind it is white on every ground.

   That is not a taste decision. A code is read by contrast and a camera expects
   the modules dark on a light field; an inverted code, light modules on ink,
   fails on most phones, which is why this cannot take the slab's own surface the
   way everything around it does. The tile is also the code's quiet zone, so its
   padding is part of the code rather than decoration around it: it is what gives
   the camera a margin of the same white the file's own border is drawn in. The
   white runs to the tile's edge and the tile is larger than the code, so the
   margin is continuous rather than a ring drawn inside a square that had already
   spent its own. How much of it the tile gives is the one number below, and it is
   tighter than the spec allows for, deliberately.

   The note used to stand under the tile, reading "Scan to open on your phone".
   It is gone because the card no longer needs to be told what it is for: a code
   sitting over the client's name is already an offer to open the site, and the
   line was spending a step of the column's height on a sentence the code states
   faster than the sentence can. The image keeps its description, so a reader who
   cannot see the code is still told where it goes; that is the `alt`, and it is
   now the only place the instruction lives.

   The card itself answers the pointer, and the tile is what moves. That is the
   second thing here that was argued rather than inherited: the note above is
   still right that this is not a control - the address is on the page twice
   already and nothing is being offered to click - so the response is the
   card's, not the code's, and the code stays a code. The tile lifts the same 2px
   the slab under it lifts and takes the same $shadow-hover, so the two read as
   one object moving rather than as two effects running at once. A reader who
   points at the code sees the code come up to meet them, which is the reading
   the code is for. */
.qr-card {
  display: block;
  margin: 0;
}

/* The lift lives on the tile because the tile is the thing with a surface: the
   figure is its box, and moving the box would move the space the column keeps
   for it. */
.qr-card:hover .qr-card-code {
  box-shadow: $shadow-hover;
  transform: translateY(-2px);
}

/* The tile. Its size is the one thing that decides whether the code works: a
   reader has to be able to point a phone at the screen and hold it there, so the
   tile is set at the size the code is still readable from and not at the size it
   would look neatest at. 8rem gives the modules inside about 4px each, which is
   a comfortable read across a desk; below about 6rem they start to blur into
   each other on a phone camera that is not perfectly steady.

   The padding is the quiet zone, and it is the one number here that is asked of
   the code rather than of the design. The file carries one module of margin of
   its own, so at 8rem the tile has to give about three more to reach the four a
   scanner expects, and 0.4rem is where that leaves the white: the ring is
   deliberately tighter than the spec's four modules because the tile grew while
   the padding shrank, and the code inside it is now larger than it was, which is
   worth more to a phone camera than the last module of margin.

   The code reads at this size on the strength of its modules being bigger, not
   of its margin being wider, so a caller that ever shrinks the tile should widen
   this again rather than let the two shrink together. */
.qr-card-code {
  background: $ground;
  border-radius: $r-md;
  display: block;
  padding: 0.4rem;
  transition: box-shadow $dur $ease, transform $dur $ease;
  width: 8rem;
}
</style>

<script>
import { useI18n } from 'vue-i18n';

export default {
  props: {
    /* The code's own file, made from the address below. */
    src: {
      type: String,
      required: true
    },
    /* The address the code encodes. It is not drawn: it is what the image is
       described as, so a reader who cannot see the code is told where it goes. */
    url: {
      type: String,
      required: true
    }
  },

  setup() {
    const { t } = useI18n({
      messages: {
        'en-MY': {
          'QR code that opens {url}': 'QR code that opens {url}'
        },
        'zh-MY': {
          'QR code that opens {url}': '可打开 {url} 的二维码'
        },
        'ms-MY': {
          'QR code that opens {url}': 'Kod QR yang membuka {url}'
        }
      }
    });

    return { t };
  }
};
</script>
